import { Container, Modal, TabItemProps, Tabs } from '@/components';
import { ProcessDetails } from '@/modules/Processes/domain';
import { WorkflowForm } from '@/modules/Workflows/components/WorkflowForm';
import { useCreateWorkflow } from '@/modules/Workflows/queries';
import { t } from 'i18next';
import { useCallback, useMemo, useState } from 'react';
import { AddWorkflowForm } from '../AddWorkflowForm';

interface ProcessWorkflowModalProps {
  process?: ProcessDetails;
  isOpen: boolean;
  handleClose: () => void;
  disableFields?: string[];
}

export const ProcessWorkflowModal = ({
  process,
  isOpen,
  handleClose,
}: ProcessWorkflowModalProps) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { submit, isSubmitting } = useCreateWorkflow(handleClose);
  const tabs: Array<TabItemProps> = useMemo(
    () => [
      { label: t('create_new_workflow') },
      {
        label: t('associate_workflow'),
      },
    ],
    [],
  );

  const onTabChange = useCallback(
    (tab: number) => {
      setSelectedTab(tab);
    },
    [setSelectedTab],
  );

  const tabList = useMemo(() => {
    if (!process) {
      return [];
    }
    return [
      {
        index: 0,
        tab: (
          <WorkflowForm
            handleClose={handleClose}
            type="create"
            handleOnSubmit={submit}
            isLoading={isSubmitting}
            processId={process.id}
            organizationId={process?.organizationId}
            companyId={process?.companyId}
            businessAreaId={process?.businessAreaId}
            disableFields={[
              'processId',
              'organizationId',
              'companyId',
              'businessAreaId',
            ]}
          />
        ),
      },
      {
        index: 1,
        tab: (
          <AddWorkflowForm
            businessAreaId={process?.businessAreaId}
            processId={process.id}
            handleClose={handleClose}
          />
        ),
      },
    ];
  }, [process, handleClose, submit, isSubmitting]);

  const elementToRender = tabList.find((el) => el.index === selectedTab);

  return (
    <Modal
      isOpen={isOpen}
      title={t('add_workflow')}
      className="h-fit w-128"
      handleClose={handleClose}
    >
      <div className="mt-6 flex flex-col gap-1">
        <Container className="mb-2">
          <Tabs activeTab={selectedTab} tabs={tabs} onTabChange={onTabChange} />
        </Container>
        {process && (
          <div className="flex flex-col gap-2">{elementToRender?.tab}</div>
        )}
      </div>
    </Modal>
  );
};
